import { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { NavHeader } from './NavHeader';
import { MonthPicker } from './MonthPicker';
import { YearPicker } from './YearPicker';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const WEEKDAY_LABELS = [
    'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
];

/**
 * Reusable month calendar grid.
 * Builds the 6-week matrix for a given month and delegates each cell
 * to the renderDay() callback — no selection logic lives here.
 *
 * Clicking the month/year label drills into a month picker, and clicking
 * the year label there drills into a year picker for quick navigation.
 *
 * Accessibility: the grid uses role="grid"/"row"/"gridcell" semantics and a
 * roving tabIndex — only one day is in the tab order at a time, and arrow
 * keys (plus Home/End/PageUp/PageDown) move focus between cells. renderDay
 * receives a `cellProps` object carrying the roving `tabIndex` and the
 * currently focused day so callers can mirror keyboard focus visually.
 *
 * @param {object}   props
 * @param {dayjs}    props.selectedDate   - used to derive the initial viewed month
 * @param {function} props.renderDay      - (day: dayjs, viewMonth: dayjs, cellProps) => ReactNode
 * @param {dayjs}    [props.maxDate]      - when set, disables "next" nav past this month
 */
export function Calendar({ selectedDate, renderDay, maxDate, onViewMonthChange, monthsWithData = [], yearsWithData = [] }) {
    const [viewMonth, setViewMonth] = useState(selectedDate.startOf('month'));
    const [calView, setCalView] = useState('days'); // 'days' | 'months' | 'years'
    // The day that currently holds keyboard focus (roving tabIndex anchor).
    const [focusedDate, setFocusedDate] = useState(selectedDate.startOf('day'));
    const gridRef = useRef(null);

    // Notify parent of the initial viewed month on mount so it can fetch dot data.
    useEffect(() => {
        onViewMonthChange?.(viewMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 6 rows × 7 cols anchored to the Sunday before the 1st of the month
    const firstCalendarDay = viewMonth.startOf('month').startOf('week');
    const calendarDays = Array.from({ length: 42 }, (_, i) => firstCalendarDay.add(i, 'day'));
    const weeks = Array.from({ length: 6 }, (_, r) => calendarDays.slice(r * 7, r * 7 + 7));

    const disableNext = maxDate
        ? !viewMonth.isBefore(maxDate, 'month')
        : false;

    // Keep focus from landing on a disabled (future) day.
    const clampToMax = (d) =>
        maxDate && d.isAfter(maxDate, 'day') ? maxDate.startOf('day') : d;

    const setMonth = (newMonth) => {
        setViewMonth(newMonth);
        onViewMonthChange?.(newMonth);
    };

    // Header nav (mouse/Enter): change month and park focus on its 1st day.
    const navigateMonth = (newMonth) => {
        setMonth(newMonth);
        setFocusedDate(clampToMax(newMonth.startOf('month')));
    };

    // Keyboard nav: move focus, following into an adjacent month if needed.
    // flushSync commits the state change before we focus, so the target cell
    // is guaranteed to be in the DOM when querySelector runs.
    const moveFocus = (newDate) => {
        const target = clampToMax(newDate);
        flushSync(() => {
            if (!target.isSame(viewMonth, 'month')) {
                setMonth(target.startOf('month'));
            }
            setFocusedDate(target);
        });
        gridRef.current
            ?.querySelector(`[data-date="${target.format('YYYY-MM-DD')}"]`)
            ?.focus();
    };

    const handleKeyDown = (e) => {
        let next;
        switch (e.key) {
            case 'ArrowLeft': next = focusedDate.subtract(1, 'day'); break;
            case 'ArrowRight': next = focusedDate.add(1, 'day'); break;
            case 'ArrowUp': next = focusedDate.subtract(7, 'day'); break;
            case 'ArrowDown': next = focusedDate.add(7, 'day'); break;
            case 'Home': next = focusedDate.startOf('week'); break;
            case 'End': next = focusedDate.endOf('week'); break;
            case 'PageUp': next = focusedDate.subtract(1, 'month'); break;
            case 'PageDown': next = focusedDate.add(1, 'month'); break;
            default: return;
        }
        e.preventDefault();
        moveFocus(next);
    };

    if (calView === 'months') {
        return (
            <MonthPicker
                key={viewMonth.year()}
                selectedDate={viewMonth}
                onSelect={(month) => {
                    navigateMonth(month);
                    setCalView('days');
                }}
                maxDate={maxDate}
                monthsWithData={monthsWithData}
                onLabelClick={() => setCalView('years')}
            />
        );
    }

    if (calView === 'years') {
        return (
            <YearPicker
                key={viewMonth.year()}
                selectedDate={viewMonth}
                onSelect={(year) => {
                    navigateMonth(viewMonth.year(year.year()).startOf('month'));
                    setCalView('months');
                }}
                maxDate={maxDate}
                yearsWithData={yearsWithData}
            />
        );
    }

    return (
        <div className="p-3 w-[280px]">
            <NavHeader
                label={viewMonth.format('MMMM YYYY')}
                onPrev={() => navigateMonth(viewMonth.subtract(1, 'month'))}
                onNext={() => navigateMonth(viewMonth.add(1, 'month'))}
                disableNext={disableNext}
                onLabelClick={() => setCalView('months')}
                prevLabel="Previous month"
                nextLabel="Next month"
                labelClick="pick a month or year"
            />
            <div
                ref={gridRef}
                role="grid"
                aria-label={viewMonth.format('MMMM YYYY')}
                className="mt-4 grid grid-cols-7"
                onKeyDown={handleKeyDown}
            >
                {/* Weekday header row */}
                <div role="row" style={{ display: 'contents' }}>
                    {WEEKDAYS.map((d, i) => (
                        <div
                            key={d}
                            role="columnheader"
                            aria-label={WEEKDAY_LABELS[i]}
                            className="flex h-9 items-center justify-center text-xs font-normal text-muted-foreground"
                        >
                            {d}
                        </div>
                    ))}
                </div>
                {/* Day cells — 6 rows of 7 */}
                {weeks.map((week, ri) => (
                    <div key={ri} role="row" style={{ display: 'contents' }}>
                        {week.map((day) =>
                            renderDay(day, viewMonth, {
                                tabIndex: day.isSame(focusedDate, 'day') ? 0 : -1,
                                focusedDate,
                            })
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
